import { useCallback } from 'react';
import {
  ApiResult,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import { useIdSearchableFetch } from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { ChangeOrder } from './ChangeOrder.interface';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useGetChangeOrderList() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<ChangeOrder>>('change-order');
  const getChangeOrders = useIdSearchableFetch(get, WorkflowEntity.ChangeOrder);
  return { data, isLoading, error, getChangeOrders };
}

export function useCreateChangeOrder() {
  const { data, isLoading, error, post } =
    useApiPost<ChangeOrder>('change-order/');
  return { data, isLoading, error, post };
}

export function useFindChangeOrderByTask(taskId: number) {
  const { data, isLoading, error, get } = useApiGet<ChangeOrder>(
    `change-order/task/${taskId}`
  );
  return { data, isLoading, error, get };
}

export function useUpdateChangeOrder(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<ChangeOrder>(
    `change-order/${id}`
  );
  return {
    updatedChangeOrder: data,
    isLoading,
    error,
    updateChangeOrder: patch
  };
}

export function useExportChangeOrderDocument(
  id: number
): [
  ApiResult<any>,
  (extension: 'docx' | 'pdf') => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<ChangeOrder>(
    `change-order/${id}/export`
  );
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (extension: 'docx' | 'pdf') => {
      // remove old document
      setBlob(undefined);
      get({ extension, timeZone });
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}
